import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type AddAddressParams,
  type CandidateAddress,
  type ConfirmDeliveryParams,
  type ContactLogEntry,
  type ContactOutcome,
  type ListPendingParams,
  type LogContactParams,
  type PendingConfirmationPage,
  type UpdateAddressParams,
  type ZoneOption,
} from "../../domain/entities/pending-confirmation";
import {
  AddAddressError,
  ConfirmDeliveryError,
  ListContactLogsError,
  ListGeoCatalogError,
  ListPendingConfirmationError,
  ListZonesError,
  LogContactError,
  UpdateAddressError,
} from "../../domain/errors/errors";
import { fetchZoneOptions } from "../../../../shared/geo/coverage-repo";
import { type ContactCenterRepository } from "../../domain/repos/contact-center-repo";
import {
  type CandidateAddressResponse,
  type ContactLogItemResponse,
  type ContactLogResponse,
  type ContactLogsResponse,
  type PendingConfirmationResponse,
  type PendingRequestResponse,
} from "../../infrastructure/responses/pending-confirmation-responses";

const toContactLog = (r: ContactLogItemResponse): ContactLogEntry => ({
  contactLogId: r.contactLogId,
  outcome: r.outcome as ContactOutcome,
  note: r.note,
  actorName: r.actorName,
  createdAt: r.createdAt,
});

const toCandidate = (c: CandidateAddressResponse): CandidateAddress => ({
  addressId: c.addressId,
  type: c.type,
  text: c.text,
  street: c.street ?? "",
  number: c.number ?? "",
  neighborhood: c.neighborhood ?? "",
  instructions: c.instructions ?? null,
  departmentName: c.departmentName ?? null,
  municipalityName: c.municipalityName ?? null,
  zoneName: c.zoneName ?? null,
  deliveryZoneCode: c.deliveryZoneCode ?? null,
  hasCoverage: c.hasCoverage === true,
});

const fullName = (c: PendingRequestResponse["customer"]): string =>
  c
    ? [c.firstName, c.middleName, c.lastName, c.secondLastName]
        .map((p) => (p ?? "").trim())
        .filter(Boolean)
        .join(" ")
    : "";

export class ContactCenterRepo implements ContactCenterRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listPending(params: ListPendingParams): Promise<PendingConfirmationPage> {
    try {
      const query = new URLSearchParams();
      query.set("status", params.status);
      query.set("page", String(params.page));
      if (params.search) query.set("q", params.search);
      const response = await this.apiClient.get<PendingConfirmationResponse>(
        `v1/requests/pending-confirmation?${query.toString()}`,
      );
      const data = response.data;
      return {
        items: (data?.items ?? []).map((r) => ({
          requestId: r.requestId,
          customerName: fullName(r.customer),
          customerPhone: r.customer?.phone ?? "",
          candidateAddresses: (r.candidateAddresses ?? []).map(toCandidate),
          deliverySlotAt: r.deliverySlotAt,
          createdAt: r.createdAt,
          contactAttempts: r.contactAttempts ?? 0,
          lastContactOutcome: (r.lastContactOutcome ??
            null) as ContactOutcome | null,
        })),
        page: data?.page ?? params.page,
        pageSize: data?.pageSize ?? 10,
        totalItems: data?.totalItems ?? 0,
        totalPages: data?.totalPages ?? 0,
        statusCounts: {
          pending: data?.statusCounts?.pending ?? 0,
          retry: data?.statusCounts?.retry ?? 0,
          process: data?.statusCounts?.process ?? 0,
        },
      };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListPendingConfirmationError(message, apiError);
    }
  }

  async listZones(): Promise<ZoneOption[]> {
    try {
      return await fetchZoneOptions(this.apiClient);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListZonesError(message, apiError);
    }
  }

  async listGeoCatalog(): Promise<ZoneOption[]> {
    try {
      return await fetchZoneOptions(this.apiClient, { includeInactive: true });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListGeoCatalogError(message, apiError);
    }
  }

  async updateAddress(params: UpdateAddressParams): Promise<void> {
    try {
      await this.apiClient.patch(
        `v1/requests/${params.requestId}/addresses/${params.addressId}`,
        {
          street: params.street,
          number: params.number,
          neighborhood: params.neighborhood,
          deliveryZoneCode: params.deliveryZoneCode || undefined,
          // Not `|| undefined`: "" is the erase, and dropping it would silently
          // turn "remove the indications" into "leave them alone".
          instructions: params.instructions,
        },
      );
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new UpdateAddressError(message, apiError);
    }
  }

  async addAddress(params: AddAddressParams): Promise<string> {
    try {
      const response = await this.apiClient.post<
        {
          street: string;
          number: string;
          neighborhood: string;
          deliveryZoneCode: string;
          instructions?: string;
        },
        { data?: { addressId?: string } }
      >(`v1/requests/${params.requestId}/addresses`, {
        street: params.street,
        number: params.number,
        neighborhood: params.neighborhood,
        deliveryZoneCode: params.deliveryZoneCode,
        instructions: params.instructions || undefined,
      });
      return response.data?.addressId ?? "";
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new AddAddressError(message, apiError);
    }
  }

  async confirm(params: ConfirmDeliveryParams): Promise<void> {
    try {
      await this.apiClient.post(
        `v1/requests/${params.requestId}/confirm-delivery`,
        {
          addressId: params.addressId,
          deliveryZoneCode: params.deliveryZoneCode,
          deliveryDate: params.deliveryDate,
          deliveryShift: params.deliveryShift,
          note: params.note || undefined,
        },
      );
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ConfirmDeliveryError(message, apiError);
    }
  }

  async listContactLogs(requestId: string): Promise<ContactLogEntry[]> {
    try {
      const response = await this.apiClient.get<ContactLogsResponse>(
        `v1/requests/${requestId}/contact-logs`,
      );
      return (response.data?.items ?? []).map(toContactLog);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListContactLogsError(message, apiError);
    }
  }

  async logContact(params: LogContactParams): Promise<ContactLogEntry> {
    try {
      const response = await this.apiClient.post<
        { outcome: string; note?: string },
        ContactLogResponse
      >(`v1/requests/${params.requestId}/contact-logs`, {
        outcome: params.outcome,
        note: params.note || undefined,
      });
      return toContactLog(response.data as ContactLogItemResponse);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new LogContactError(message, apiError);
    }
  }
}
